<template>
  <content-dialog
    v-model="dialogOpen"
    title="Staff Availability"
    width="600px"
    min-height="20vh"
    max-height="70vh"
    close-icon="mdi-arrow-left"
    fullscreen-mobile
    :loading="loading"
    @hide="$emit('hide')"
  >
    <!-- Users of this appointment -->
    <q-list v-if="!loading" separator>
      <sticky-header>
        This Appointment
      </sticky-header>
      <DateTimePartial
        class="q-px-md q-py-xs"
        :start-date="startDate"
        :end-date="endDate"
        :all-day="allDay"
        :is-scheduled="isScheduled"
        :timezone="timezone"
      />
      <no-results
        v-if="attendees.length < 1"
        title="No existing staff to display"
        icon="mdi-account-off"
      />
      <page-banner
        v-if="allStaffAvailable"
        dense
        color="green-2"
        align="center"
        class="q-mx-sm"
        label="All selected staff are available in this timeslot"
      />

      <StaffItem
        v-for="(user, key) in attendees"
        :key="'result-' + key"
        :user="user"
        :availability="results[user.id]"
        :can-edit="withEditing"
        @remove="$emit('remove', user)"
      />
    </q-list>

    <!-- Suggestions -->
    <q-list
      v-if="!loading && suggestions.length > 0 && withEditing"
      class="q-mt-lg"
      separator
    >
      <sticky-header color="grey-7">
        Suggestions
      </sticky-header>
      <q-item-label class="q-px-md q-pb-sm" caption>
        The following staff are available in your selected timeslot. Click on a user to add them to the appointment.
      </q-item-label>
      <user-item
        v-for="(user, key) in suggestions"
        :key="'suggestion-' + key"
        :user="user"
        without-email
        clickable
        @click="addUserToAppointment(user, key)"
      >
        <q-icon name="mdi-arrow-up" />
      </user-item>
    </q-list>

    <template #bottom>
      <q-toolbar>
        <q-space />
        <q-btn
          v-close-popup
          label="Close"
          flat
          :disable="loading"
        />
      </q-toolbar>
    </template>
  </content-dialog>

  <message-dialog
    v-model="confirmDialogOpen"
    :title="withEditing ? 'Confirm' : 'Warning'"
  >
    <q-item-label>Some staff may not be available at this appointment time.</q-item-label>
    <q-item-label v-if="withEditing">
      Do you want to continue saving this appointment?
    </q-item-label>

    <template #actions>
      <q-btn
        v-close-popup
        color="black"
        flat
        :autofocus="!withEditing"
        :label="withEditing ? 'Cancel' : 'View Staff'"
        @click="dialogOpen = true"
      />
      <q-btn
        v-if="withEditing"
        v-close-popup
        color="blue"
        flat
        autofocus
        label="Continue"
        @click="$emit('continue')"
      />
    </template>
  </message-dialog>
</template>

<script>
import StaffItem from './StaffItem'
import staffMixin from '../mixins/staffMixin'
import DateTimePartial from './../../partials/DateTime'

export default {
  components: {
    StaffItem, DateTimePartial
  },
  mixins: [staffMixin],
  props: {
    id: {
      type: Number
    },
    attendees: {
      type: Array,
      default: () => {
        return []
      }
    },
    startDate: {
      type: String
    },
    endDate: {
      type: String
    },
    allDay: {
      type: Boolean,
      default: false
    },
    isScheduled: {
      type: Boolean,
      default: true
    },
    timezone: {
      type: [Object, String],
      required: true
    },
    withSuggestions: {
      type: Boolean,
      default: false
    },
    withEditing: {
      type: Boolean,
      default: false
    }
  },
  emits: ['hide', 'remove', 'continue', 'add'],
  data () {
    return {
      loading: false,
      dialogOpen: false,
      confirmDialogOpen: false,
      results: {},
      suggestions: []
    }
  },
  computed: {
    allStaffAvailable () {
      return this.attendees.length > 0 && Object.keys(this.results).length > 0 && Object.values(this.results).filter(item => item !== 'available').length < 1
    }
  },
  methods: {
    async open (silent = false) {
      if (!silent) {
        this.dialogOpen = true
      }
      return await this.checkAvailability()
    },
    confirm () {
      this.confirmDialogOpen = true
    },
    addUserToAppointment (user, key) {
      this.$emit('add', user)
      this.suggestions.splice(key, 1)
      this.$q.notify({
        color: 'green',
        message: 'Staff member added to appointment'
      })
    },
    /* Check whether to prompt the user to verify the staff */
    async checkAvailability () {
      this.results = {}
      this.suggestions = []
      this.loading = true

      const { results, suggestions } = await this.baseCheckAvailability(
        this.attendees.map(item => item.id),
        this.startDate,
        this.endDate,
        this.id || null,
        this.timezone,
        this.withSuggestions
      )

      this.results = results
      this.suggestions = suggestions
      this.loading = false
      return Object.values(this.results).filter(item => item !== 'available').length > 0
    }
  }
}
</script>
